import React from 'react';
import { 
  Facebook, 
  Twitter, 
  Instagram, 
  Linkedin, 
  Youtube,
  ChevronUp,
  MessageCircle,
  Heart,
  Leaf,
  Flower2,
  Sprout,
  TreeDeciduous
} from 'lucide-react';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="relative bg-[#679729] text-white">
      {/* Organic Decorative Elements */}
      <div className="absolute top-0 left-0 w-full overflow-hidden opacity-10">
        <div className="flex justify-between px-4">
          <Flower2 size={64} className="transform -rotate-45" />
          <Leaf size={48} className="transform rotate-45" />
          <Sprout size={56} />
          <TreeDeciduous size={48} className="transform -rotate-12" />
          <Flower2 size={64} className="transform rotate-45" />
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto py-16 px-5">
        {/* Main Navigation Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 mb-12">
          {/* Company Links */}
          <div>
            <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
              Company
              <Leaf size={16} className="text-white/70" />
            </h3>
            <ul className="space-y-4">
              <li>
                <a href="/" className="group flex items-center gap-2 hover:text-white/80 transition-all duration-300">
                  <span className="transform group-hover:translate-x-1 transition-transform duration-200">Home</span>
                </a>
              </li>
              <li>
                <a href="/about" className="group flex items-center gap-2 hover:text-white/80 transition-all duration-300">
                  <span className="transform group-hover:translate-x-1 transition-transform duration-200">About Us</span>
                </a>
              </li>
              <li>
                <a href="/contact" className="group flex items-center gap-2 hover:text-white/80 transition-all duration-300">
                  <span className="transform group-hover:translate-x-1 transition-transform duration-200">Contact Us</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
              Resources
              <TreeDeciduous size={16} className="text-white/70" />
            </h3>
            <ul className="space-y-4">
              <li>
                <a href="/blogs" className="group flex items-center gap-2 hover:text-white/80 transition-all duration-300">
                  <span className="transform group-hover:translate-x-1 transition-transform duration-200">Blogs</span>
                </a>
              </li>
              <li>
                <a href="/careers" className="group flex items-center gap-2 hover:text-white/80 transition-all duration-300">
                  <span className="transform group-hover:translate-x-1 transition-transform duration-200">Careers</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
              Connect With Us
              <Flower2 size={16} className="text-white/70" />
            </h3>
            <div className="space-y-6">
              <div className="flex gap-4">
                <a href="https://www.facebook.com/profile.php?id=61573042873807" className="bg-white/10 p-3 rounded-full hover:bg-white/20 transition-all duration-300 group">
                  <Facebook size={20} className="group-hover:scale-110 transition-transform duration-200" />
                </a>
                <a href="https://x.com/OrganicPads" className="bg-white/10 p-3 rounded-full hover:bg-white/20 transition-all duration-300 group">
                  <Twitter size={20} className="group-hover:scale-110 transition-transform duration-200" />
                </a>
                <a href="https://www.instagram.com/organicpads_/" className="bg-white/10 p-3 rounded-full hover:bg-white/20 transition-all duration-300 group">
                  <Instagram size={20} className="group-hover:scale-110 transition-transform duration-200" />
                </a>
                <a href="https://www.youtube.com/channel/UCG5-R0zTI-2DAM14ZC0ZD-A" className="bg-white/10 p-3 rounded-full hover:bg-white/20 transition-all duration-300 group">
                  <Youtube size={20} className="group-hover:scale-110 transition-transform duration-200" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Legal Links */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-wrap justify-center gap-6 text-sm mb-8">
            <a href="/disclaimer" className="hover:text-white/80 transition-colors duration-200">Disclaimer</a>
            <span className="text-white/30">•</span>
            <a href="/privacy-policy" className="hover:text-white/80 transition-colors duration-200">Privacy Policy</a>
            <span className="text-white/30">•</span>
            <a href="/refund-policy" className="hover:text-white/80 transition-colors duration-200">Refund Policy</a>
            <span className="text-white/30">•</span>
            <a href="/terms-of-service" className="hover:text-white/80 transition-colors duration-200">Terms of Service</a>
            <span className="text-white/30">•</span>
            <a href="/cookie-policy" className="hover:text-white/80 transition-colors duration-200">Cookie Policy</a>
          </div>

          {/* Copyright */}
          <div className="text-center space-y-2">
            <p className="text-sm opacity-90">
              © Organic Pads. {new Date().getFullYear()}. All rights reserved.
            </p>
            <p className="text-sm opacity-90 flex items-center justify-center gap-1">
              Made with <Heart size={14} className="text-green-500 animate-pulse" /> for a healthier & eco-friendly future.
            </p>
          </div>

        </div>
      </div>

      {/* Fixed Buttons */}
      <div className="fixed bottom-6 right-6 flex flex-col gap-2">
        <button 
          onClick={scrollToTop}
          className="bg-white text-[#679729] p-3 rounded-full shadow-lg hover:bg-gray-100 transition-all duration-300 group"
        >
          <span className="sr-only">Scroll to top</span>
          <ChevronUp size={20} className="group-hover:scale-110 transition-transform duration-200" />
        </button>
      </div>
    </footer>
  );
};

export default Footer;